import type { ScoreMode } from '../contexts/ScoreModeContext';

/** Presentation metadata for a score mode. */
export interface ModeMeta {
  /** User-facing label, e.g. "Delivery". */
  label: string;
  /** Plain-language one-liner describing what the lens scores. */
  blurb: string;
  /** The opposite mode — used by toggles and inline "switch" links. */
  other: ScoreMode;
  /** Singular noun for the lens's scored item ("Repo" / "Feature"). */
  itemNoun: string;
  /** Plural noun for the lens's scored item ("Repos" / "Features"). */
  itemNounPlural: string;
  /** Stat-card label for the lens average ("Delivery avg" / "Discovery avg"). */
  avgLabel: string;
  /** Top-10 module title ("Top 10 repos" / "Top 10 features"). */
  top10Title: string;
  /** Top-10 item-column header ("Repo" / "Feature"). */
  itemHeader: string;
  /** PhaseCard blurb ("Analyze specs in repos" / "…in Rally features"). */
  cardBlurb: string;
}

/**
 * Single source of truth for each mode's user-facing label + blurb, reused by the
 * toggle gloss (FR-1/AC-1.1), the persistent mode indicator (AC-1.2), and the
 * lens-aware empty states (AC-1.3/AC-1.4). The per-lens nouns/labels/titles below
 * replace the hand-rolled `mode === 'discovery' ? … : …` ternaries that used to
 * pick them at each call site (P2/FR-2), keeping the copy from diverging.
 */
export const MODE_META: Record<ScoreMode, ModeMeta> = {
  delivery: {
    label: 'Delivery',
    blurb: 'your shipped code repos',
    other: 'discovery',
    itemNoun: 'Repo',
    itemNounPlural: 'Repos',
    avgLabel: 'Delivery avg',
    top10Title: 'Top 10 repos',
    itemHeader: 'Repo',
    cardBlurb: 'Analyze specs in repos',
  },
  discovery: {
    label: 'Discovery',
    blurb: 'your Rally planning & specs',
    other: 'delivery',
    itemNoun: 'Feature',
    itemNounPlural: 'Features',
    avgLabel: 'Discovery avg',
    top10Title: 'Top 10 features',
    itemHeader: 'Feature',
    cardBlurb: 'Analyze specs in Rally features',
  },
};

/** Getter for a mode's presentation metadata. */
export function modeMeta(mode: ScoreMode): ModeMeta {
  return MODE_META[mode];
}
